import { NavLink, Outlet } from 'react-router';
import { PERMISSIONS, type Permission } from '@virtuetasks/types';
import { cn } from '@/lib/utils';
import { useCan } from '@/features/auth';

interface Tab {
  to: string;
  label: string;
  permission?: Permission;
}

const TABS: Tab[] = [
  { to: '/settings/profile', label: 'Profile' },
  { to: '/settings/notifications', label: 'Notifications' },
  // V5-03 — personal, like Profile and Notifications: everybody has a calendar.
  { to: '/settings/calendar', label: 'Calendar' },
  { to: '/settings/members', label: 'Members', permission: PERMISSIONS.USER_LIST },
  { to: '/settings/teams', label: 'Teams', permission: PERMISSIONS.USER_LIST },
  { to: '/settings/labels', label: 'Labels', permission: PERMISSIONS.LABEL_MANAGE },
  { to: '/settings/workspace', label: 'Workspace', permission: PERMISSIONS.WORKSPACE_UPDATE },
  { to: '/settings/automations', label: 'Automations', permission: PERMISSIONS.AUTOMATION_MANAGE },
  { to: '/settings/integrations', label: 'Integrations', permission: PERMISSIONS.SETTINGS_INTEGRATIONS },
  // V5-07 — read-only numbers, gated like the rest of workspace settings.
  { to: '/settings/usage', label: 'Usage', permission: PERMISSIONS.WORKSPACE_VIEW },
  { to: '/settings/audit', label: 'Audit', permission: PERMISSIONS.AUDIT_VIEW },
];

/** Tabs hide what the user cannot use — UX only; the server re-checks (§3.4). */
export function SettingsLayout() {
  const can = useCan();
  const visible = TABS.filter((t) => !t.permission || can(t.permission));

  return (
    <div className="flex min-h-full flex-col">
      <nav
        className="flex gap-0.5 overflow-x-auto border-b border-[var(--border)] px-4 pt-3"
        aria-label="Settings sections"
      >
        {visible.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              cn(
                'whitespace-nowrap rounded-t-[6px] border-b-2 px-3 py-1.5 text-[13px] transition-colors duration-[120ms]',
                isActive
                  ? 'border-[var(--accent)] font-medium text-[var(--text)]'
                  : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text)]',
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}
